import classNames from 'classnames';
import React, { useRef, useState, useEffect } from 'react';
import style from './IndexCollectionManagementTable.module.scss';
import InitTable, { Query } from '../Table/Table';
import { InputType } from '../Table/components/Editor/Editor';
import { Link } from 'react-router-dom';
import { getIndexTableCollections, getSystemTaxYears } from '../../services';
import { useAppSelector } from '../../redux/store';
import { IndexTableCollection, SelectOption, State } from '../../interface';
import _ from 'lodash';
import FieldToggleCheckbox from '../FieldToggleCheckbox/FieldToggleCheckbox';


const { Table, Column, TopBar } = InitTable<IndexTableCollection>();

interface Props {
  className?: string;
}


function IndexCollectionManagementTable({
  className
}: Props) {
  const stateOptions = useAppSelector(state => (state.states ?? []).map((o: State) => ({
    value: o.id,
    label: o.abbreviation,
    name: o.abbreviation,
  })));
  const [fetchingAllTaxYears, setFetchingAllTaxYears] = useState(false);
  const [activeTaxYears, setActiveTaxYears] = useState<SelectOption[]>([]);
  const [currentTaxYear, setCurrentTaxYear] = useState<number | null>(null);
  const [indexCollections, setIndexCollections] = useState<IndexTableCollection[]>([]);
  const [showOnlyCurrentTaxYear, setShowOnlyCurrentTaxYear] = useState<boolean>(true);

  useEffect(() => {
    setFetchingAllTaxYears(true);
    getSystemTaxYears()
      .then((res) => {
        setFetchingAllTaxYears(false);
        const allActiveTaxYears = _.orderBy(
          res.data,
          ['taxYear'],
          ['desc'],
        );

        setActiveTaxYears(allActiveTaxYears.map(taxYear => { 
          return { 
            item: taxYear, 
            label: taxYear.taxYear.toString(), 
            name: taxYear.taxYear.toString(), 
            value: taxYear.taxYear 
          } as SelectOption; 
        }));

        let currentTaxYearIndex = _.findIndex(allActiveTaxYears, {
          isCurrent: true,
        });
        if (currentTaxYearIndex < 0) {
          currentTaxYearIndex = 0;
        }
        setCurrentTaxYear(allActiveTaxYears[currentTaxYearIndex].taxYear);
      })
      .catch(() => {
        setFetchingAllTaxYears(false);
      });
  }, []);

  useEffect(() => {
    fetchData(previousQuery.current);
  }, [showOnlyCurrentTaxYear, currentTaxYear]);
  
  //Pagination
  const [total, setTotal] = useState<number>(0);
  const [fetchingData, setFetchingData] = useState(false);
  const previousQuery = useRef<Query | undefined>(undefined);

  const fetchData = (query?: Query) => {
    if (!currentTaxYear){
      return;
    }

    const queryTaxYear = showOnlyCurrentTaxYear ? currentTaxYear : (query?.filter?.taxYear as number)
    previousQuery.current = query;
    setFetchingData(true);
    getIndexTableCollections({
      stateIds: (query?.filter?.stateIds as number[])?.join(','),
      text: query?.search,
      taxYear: queryTaxYear ?? null,
      sortBy: _.last(_.keys(query?.order)),
      sortDirection: _.last(_.values(query?.order)),
      page: query?.pagination?.page ?? 1,
      perPage: query?.pagination?.pageSize ?? 10,
    })
      .then((res) => {
        setIndexCollections(res.data.items);
        setTotal(res.data.total);
        setFetchingData(false);
      })
      .catch(() => {
        setFetchingData(false);
      });

  };

  return (
    <div className={
      classNames(
        className,
        style['main-content'],
        'IndexCollectionSearch d-flex flex-column'
      )
    }>
      <Table
        id='IndexCollectionManagementTable'
        rows={indexCollections}
        onQueryChanged={fetchData}
        paginate
        sortable
        totalRows={total}
        loading={fetchingData || fetchingAllTaxYears}
        searchable='Search Index Collection...'
        dynamicColumns
      >
        <TopBar>
          <FieldToggleCheckbox
            label='Show Only Current Tax Year'
            value={showOnlyCurrentTaxYear}
            onChange={(value) => setShowOnlyCurrentTaxYear(value)}
            classnames={style['float-right']}
          />
        </TopBar>
        
        {/* Collection Name */}
        <Column
          label='Collection Name'
          id='name'
        >
          {
            (row: IndexTableCollection) => (
              <span><strong>{row.name}</strong></span>
            )
          }
        </Column>
        
        {/* Collection State */}
        <Column
          label='Collection State'
          id="stateIds"
          prop="state.abbreviation"
          sortKey='state.abbreviation'
          filterable={{
            type: InputType.Select,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            options: stateOptions,
            multi: true,
            searchable: true,
          }}
        />

        {/* Tax Year */}
        <Column
          label='Tax Year'
          prop='taxYear'
          sortKey='taxYear'
          filterable={{
            type: InputType.Select,
            getValue: (x) => (x as SelectOption).value,
            getLabel: (x) => (x as SelectOption).label,
            options: activeTaxYears,
            multi: false,
            searchable: true,
          }}
        />

        {/* Actions column  */}
        <Column
          id='actions'
          label='Actions'
          sortable={false}
        >
          {(data: IndexTableCollection) => (
            <div className={classNames(style['operate'], 'd-flex')}>
              <Link
                to={`/admin/index-collection-management/${data.id}/view`}
                className='view'
              />
            </div>
          )}
        </Column>
      </Table>
    </div>
  );
}

export default IndexCollectionManagementTable;